import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
  type CountryCode,
} from 'libphonenumber-js';
import { Check, ChevronDown } from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

const PRIORITY_COUNTRIES: CountryCode[] = ['US', 'CA', 'GB', 'AU', 'MX'];

/** Format a string of digits (or partial number) as a national phone number. */
export function formatPhoneDigits(digits: string, country: CountryCode): string {
  if (!digits) return '';
  return new AsYouType(country).input(digits);
}

/**
 * Parse a stored E.164 phone string (e.g. "+15551234567") into a country code
 * and a formatted national number.  Falls back to { country: 'US', national: raw }
 * if the string can't be parsed.
 */
export function parseStoredPhone(stored: string | null | undefined): {
  country: CountryCode;
  national: string;
} {
  if (!stored) return { country: 'US', national: '' };
  try {
    const parsed = parsePhoneNumber(stored);
    if (parsed?.country) {
      return {
        country: parsed.country as CountryCode,
        national: formatPhoneDigits(parsed.nationalNumber, parsed.country as CountryCode),
      };
    }
  } catch {
    // fall through
  }
  return { country: 'US', national: stored };
}

/** Convert a (national, country) pair to E.164. Returns the national string as-is on failure. */
export function phoneToE164(national: string, country: CountryCode): string {
  try {
    const digits = national.replace(/\D/g, '');
    if (!digits) return '';
    const parsed = parsePhoneNumber(digits, country);
    if (parsed) return parsed.format('E.164');
  } catch {
    // fall through
  }
  return national;
}

// ── Flag emoji ────────────────────────────────────────────────────────────────

function flagEmoji(code: string): string {
  return String.fromCodePoint(
    ...code.toUpperCase().split('').map(c => c.charCodeAt(0) + 127397),
  );
}

// ── Custom country selector (md+ screens only) ────────────────────────────────

interface CountrySelectProps {
  value: CountryCode;
  options: CountryCode[];
  onChange: (c: CountryCode) => void;
  disabled?: boolean;
}

function CountrySelect({ value, options, onChange, disabled }: CountrySelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    function handleOutside(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  // Auto-focus search box when dropdown opens
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => searchRef.current?.focus(), 10);
      return () => clearTimeout(t);
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter(c => {
      const name = (regionNames.of(c) ?? c).toLowerCase();
      return (
        name.includes(q) ||
        c.toLowerCase().includes(q) ||
        `+${getCountryCallingCode(c)}`.includes(q)
      );
    });
  }, [query, options]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  }

  const callingCode = `+${getCountryCallingCode(value)}`;

  return (
    <div ref={containerRef} className="relative shrink-0 hidden md:block">
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(v => !v)}
        className={`h-11 pl-3 pr-2.5 border border-stone border-r-0 rounded-l-lg bg-mist
                    flex items-center gap-1.5
                    focus:outline-none transition-colors duration-150
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${open ? 'border-plum ring-2 ring-plum/20' : 'hover:border-plum/60'}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select country calling code"
      >
        <span className="text-base leading-none select-none" aria-hidden="true">
          {flagEmoji(value)}
        </span>
        <span className="font-mono text-[13px] tabular-nums text-midnight">{callingCode}</span>
        <ChevronDown
          size={13}
          className={`text-slate transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          className="absolute top-full left-0 z-50 mt-1
                     w-72 bg-white border border-stone rounded-xl
                     shadow-brand-md flex flex-col overflow-hidden"
          style={{ maxHeight: '280px' }}
          onKeyDown={handleKeyDown}
        >
          {/* Search */}
          <div className="px-2.5 py-2 border-b border-stone shrink-0">
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search country or code…"
              className="w-full h-8 px-2.5 rounded-lg border border-stone bg-mist
                         text-[13px] text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum transition-colors duration-150"
            />
          </div>

          {/* Options */}
          <div role="listbox" className="overflow-y-auto flex-1 overscroll-contain">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-center text-[13px] text-slate">No results</p>
            ) : (
              filtered.map(c => {
                const isSelected = c === value;
                return (
                  <button
                    key={c}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => { onChange(c); setOpen(false); setQuery(''); }}
                    className={`w-full text-left px-3 py-2.5 flex items-center gap-2.5
                                transition-colors duration-100
                                ${isSelected ? 'bg-plum/5' : 'hover:bg-mist active:bg-stone'}`}
                  >
                    <span className="text-base leading-none shrink-0 select-none" aria-hidden="true">
                      {flagEmoji(c)}
                    </span>
                    <span className={`flex-1 min-w-0 truncate text-[13px]
                                     ${isSelected ? 'font-medium text-plum' : 'text-midnight'}`}>
                      {regionNames.of(c) ?? c}
                    </span>
                    <span className="font-mono text-[12px] text-slate shrink-0">
                      +{getCountryCallingCode(c)}
                    </span>
                    {isSelected && <Check size={12} className="shrink-0 text-plum ml-1" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

interface PhoneInputProps {
  id: string;
  country: CountryCode;
  value: string;
  onCountryChange: (c: CountryCode) => void;
  onValueChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function PhoneInput({
  id,
  country,
  value,
  onCountryChange,
  onValueChange,
  placeholder,
  disabled,
}: PhoneInputProps) {
  const countryOptions = useMemo(() => {
    const all = getCountries();
    const priority = PRIORITY_COUNTRIES.filter(c => all.includes(c));
    const rest = all
      .filter(c => !PRIORITY_COUNTRIES.includes(c))
      .sort((a, b) =>
        (regionNames.of(a) ?? a).localeCompare(regionNames.of(b) ?? b),
      );
    return [...priority, ...rest];
  }, []);

  function handleInput(e: React.ChangeEvent<HTMLInputElement>) {
    const digits = e.target.value.replace(/\D/g, '');
    onValueChange(formatPhoneDigits(digits, country));
  }

  function handleCountryChange(c: CountryCode) {
    onCountryChange(c);
    onValueChange('');
  }

  return (
    <div className="flex">
      {/* Mobile: native select (hidden on md+) */}
      <select
        value={country}
        onChange={e => handleCountryChange(e.target.value as CountryCode)}
        disabled={disabled}
        aria-label="Country calling code"
        className="md:hidden shrink-0 h-11 pl-2 pr-1 border border-stone border-r-0 rounded-l-lg bg-mist
                   text-small text-midnight focus:outline-none focus:border-plum
                   transition-colors duration-150"
      >
        {countryOptions.map(c => (
          <option key={c} value={c}>
            +{getCountryCallingCode(c)} {regionNames.of(c) ?? c}
          </option>
        ))}
      </select>

      {/* md+: custom styled dropdown */}
      <CountrySelect
        value={country}
        options={countryOptions}
        onChange={handleCountryChange}
        disabled={disabled}
      />

      {/* Phone number input */}
      <input
        id={id}
        type="tel"
        value={value}
        onChange={handleInput}
        disabled={disabled}
        placeholder={placeholder ?? 'Phone number'}
        autoComplete="tel-national"
        className="flex-1 min-w-0 h-11 px-3 border border-stone rounded-r-lg bg-white
                   text-small text-midnight placeholder:text-slate
                   focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                   transition-colors duration-150"
      />
    </div>
  );
}
